<template>
  <div
    class="ingroup grid max-w-screen-md flex-1"
    :class="{
      'grid-cols-[90px_1fr_75px_40px] md:grid-cols-[140px_400px_118px_40px_70px]': !isMobile,
      'grid-cols-[1fr_40px]': isMobile,
    }"
  >
    <TopbarMarketSelector
      v-if="!isMobile"
      v-model="selectedMarketplace"
      :options="marketplaces"
      class="ingroup-action-field"
      @on-marketplace-select="showMarketplaces"
    />
    <TopbarSearchInput
      v-model="keyword"
      class="ingroup-action-field"
      :class="{
        'rounded-[6px]': isMobile,
      }"
      @on-enter="redirectToKeywordTool"
    />
    <TopbarCountrySelector
      v-if="!isMobile"
      v-model="selectedCountry"
      :countries="countries"
      class="ingroup-action-field"
      @on-show-countries="showCountries"
    />
    <TopbarSearchButton
      :disabled="!keyword?.trim()"
      class="ingroup-action-field"
      @on-search="handleOnSearch"
    />
    <div class="hidden flex-col justify-between pl-4 md:flex">
      <span class="text-tiny-medium text-[--color-neutral-neutral-light]">Searches:</span>
      <span class="text-small-medium flex items-center gap-1 text-[--color-neutral-neutral-dark]">{{ quota }}<ContextualHelp
        :content="t('layout.tooltip.text')"
        trigger-icon-class="text-[--color-neutral-neutral-light]"
        placement="bottom"
      /></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCookies } from '@vueuse/integrations/useCookies';
import { storeToRefs } from 'pinia';
import { useDialog } from 'primevue/usedialog';
import { ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import ContextualHelp from '@/common/components/molecules/ContextualHelp';
import { useMobile } from '@/common/composables/application';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { useSearchParams } from '@/modules/keywords/composables/router';
import TopbarCountrySelector from '@/modules/layout/components/atoms/TopbarCountrySelector';
import TopbarMarketSelector from '@/modules/layout/components/atoms/TopbarMarketSelector';
import TopbarSearchButton from '@/modules/layout/components/atoms/TopbarSearchButton';
import TopbarSearchInput from '@/modules/layout/components/atoms/TopbarSearchInput';
import CountriesSelection from '@/modules/layout/components/organisms/CountriesSelection';
import MarketplacesSelection from '@/modules/layout/components/organisms/MarketplacesSelection';
import { useTopbarStore } from '@/modules/layout/stores/topbar';

defineProps<{
  quota?: string;
}>();

const cookies = useCookies(['searchCountry']);

const topbarStore = useTopbarStore();
const { selectedCountry, selectedMarketplace, marketplaces, countries } = storeToRefs(topbarStore);
const dialog = useDialog();
const router = useRouter();
const { searchParams } = useSearchParams();
const toast = useTrackedToast();
const { t } = useI18n();
const { isMobile } = useMobile();

const keyword = ref<string>('');

watchEffect(() => {
  keyword.value = searchParams.value.keyword ?? '';
});

const showMarketplaces = () => {
  dialog.open(MarketplacesSelection, {
    props: {
      modal: true,
      closable: false,
      showHeader: false,
      contentClass: 'p-0 rounded-t-md',
    },
    data: {
      marketplaces: marketplaces.value,
      selectedMarketplace: selectedMarketplace.value,
    },
    onClose: ({ data }) => {
      if (data?.selectedMarketplace) {
        selectedMarketplace.value = data.selectedMarketplace;
      }
    },
  });
};

const showCountries = () => {
  dialog.open(CountriesSelection, {
    props: {
      modal: true,
      closable: false,
      showHeader: false,
      contentClass: 'p-0 rounded-t-md',
    },
    data: {
      countries: countries.value,
      selectedCountry: selectedCountry.value,
    },
    onClose: ({ data }) => {
      if (data?.selectedCountry) {
        selectedCountry.value = data.selectedCountry;
      }
    },
  });
};

function handleOnSearch() {
  redirectToKeywordTool();
}

function redirectToKeywordTool() {
  if (!keyword.value?.trim()) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Please enter a keyword', errorCode: 'KEYWORD_REQUIRED' });
    return;
  }

  cookies.set('searchCountry', selectedCountry.value, { path: '/' });

  router.push({
    path: '/keyword-tool',
    query: {
      keyword: keyword.value.trim(),
      country: selectedCountry.value,
      source: selectedMarketplace.value.code,
    },
  });
}
</script>

<style scoped>
.ingroup:focus-within .ingroup-action-field {
  outline: 1px solid #336D5E !important;
  outline-offset: 0px !important;
}

.ingroup:hover .ingroup-action-field {
  outline: 1px solid #99B6AF;
  outline-offset: 0px;
}
</style>